import React, { useState, useRef, useEffect, useContext } from 'react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import '../../../../assests/css/admin/DisputeManagement/DisputeDetail/DisputeDetail.css';

const DisputeDetail = ({ dispute, onBack, onUpdateStatus }) => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);

  const [activeTab, setActiveTab]               = useState('messages');
  const [message, setMessage]                   = useState('');
  const [messages, setMessages]                 = useState([]);
  const [conversationId, setConversationId]     = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryImages, setGalleryImages]       = useState([]);
  const [selectedImage, setSelectedImage]       = useState(null);
  const [imagePreview, setImagePreview]         = useState(null);
  const fileInputRef = useRef(null);

  const fetchConversation = () => {
    if (!baseUrl) return;

    const admin_id = localStorage.getItem('admin_id');

    axios
      .post(`${baseUrl}/admin/HelpandSupport/fetchConversation`, {
        mlhelpsupport_id: dispute.id,
        mlcustomer_id:    dispute.Mlcustomer_id,
        mlorder_id:       dispute.Mlorder_id,
        admin_id:         admin_id,
      })
      .then((response) => {
        if (response.data.success) {
          const conversation = response.data.conversation;
          setConversationId(conversation.id);
          setMessages(conversation.Mlchats || conversation.mlchats || []);
        }
      })
      .catch((error) => {
        console.error('fetchConversation error:', error);
      });
  };

  useEffect(() => {
    fetchConversation();
  }, [baseUrl, dispute.id]);

  // Poll for new messages every 3 seconds so customer replies show up live
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversation();
    }, 3000);
    return () => clearInterval(interval);
  }, [baseUrl, dispute.id]);

  // ── Image select for chat ──
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendMessage = () => {
    if (!message.trim() && !selectedImage) return;
    if (!conversationId) return;

    const payload = new FormData();
    payload.append('Mlconversation_id', conversationId);
    payload.append('message', message);
    if (selectedImage) payload.append('image', selectedImage);

    axios
      .post(`${baseUrl}/admin/HelpandSupport/sendMessage`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    .then((response) => {
  if (response.data.success) {
    setMessages((prev) => [...prev, response.data.chat]);
    setMessage('');
    handleRemoveImage();
  }
})
      .catch((error) => {
        console.error('sendMessage error:', error);
      });
  };

  const handleAttachmentClick = () => fileInputRef.current?.click();

  const getImageUrl = (path) => (path.startsWith('http') ? path : `${baseUrlForFiles}/${path}`);

  const openImageGallery = (imagePath) => {
    setGalleryImages([getImageUrl(imagePath)]);
    setShowImageGallery(true);
  };

  const getStatusClass = (status) => {
    const map = {
      'open':      'Dispute-Admin-Dispute-status-open',
      'pending':   'Dispute-Admin-Dispute-status-pending',
      'escalated': 'Dispute-Admin-Dispute-status-escalated',
      'closed':    'Dispute-Admin-Dispute-status-resolved',
    };
    return map[(status || '').toLowerCase()] || '';
  };

  return (
    <div className="Dispute-Admin-Dispute-detail-container">

      {/* Breadcrumb */}
      <div className="Dispute-Admin-Dispute-detail-breadcrumb">
        <span className="Dispute-Admin-Dispute-detail-breadcrumb-link">Disputes</span>
        <span className="Dispute-Admin-Dispute-detail-breadcrumb-separator">/</span>
        <span className="Dispute-Admin-Dispute-detail-breadcrumb-current">Case #{dispute.id}</span>
      </div>

      {/* Header */}
      <div className="Dispute-Admin-Dispute-detail-header">
        <div>
          <div className="Dispute-Admin-Dispute-detail-title-row">
            <h1 className="Dispute-Admin-Dispute-detail-title">Case #{dispute.id}</h1>
            <span className={`Dispute-Admin-Dispute-detail-status-badge ${getStatusClass(dispute.status)}`}>
              {(dispute.status || 'N/A').toUpperCase()}
            </span>
          </div>
          <p className="Dispute-Admin-Dispute-detail-claim">
            Subject: {dispute.subject || 'N/A'}
          </p>
        </div>
        <button className="Dispute-Admin-Dispute-detail-back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 6l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Back to list
        </button>
      </div>

      {/* 2-COLUMN GRID */}
      <div className="Dispute-Admin-Dispute-detail-grid">

        {/* LEFT COLUMN — Ticket Summary (compact) */}
        <div className="Dispute-Admin-Dispute-detail-left-column">
          <div className="Dispute-Admin-Dispute-detail-card">
            <h3 className="Dispute-Admin-Dispute-detail-card-title">TICKET SUMMARY</h3>

            <div className="Dispute-Admin-Dispute-detail-order-info">
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Ticket ID</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">#{dispute.id}</span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Order ID</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">
                  {dispute.Mlorder_id ? `#${dispute.Mlorder_id}` : 'N/A'}
                </span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Category</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">{dispute.category || 'N/A'}</span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Status</span>
                <span className={`Dispute-Admin-Dispute-status-badge ${getStatusClass(dispute.status)}`}>
                  {(dispute.status || 'N/A').toUpperCase()}
                </span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Created</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">
                  {dispute.created_at
                    ? new Date(dispute.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Conversation</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">
                  {conversationId ? `#${conversationId}` : 'N/A'}
                </span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Customer</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">{dispute.customer_name || 'N/A'}</span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Email</span>
                <span className="Dispute-Admin-Dispute-detail-order-value Dispute-Admin-Dispute-detail-order-value--small">
                  {dispute.customer_email || 'N/A'}
                </span>
              </div>
              <div className="Dispute-Admin-Dispute-detail-order-row">
                <span className="Dispute-Admin-Dispute-detail-order-label">Assigned</span>
                <span className="Dispute-Admin-Dispute-detail-order-value">{dispute.admin_name || 'Unassigned'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Messages (large) */}
        <div className="Dispute-Admin-Dispute-detail-center-column">
          <div className="Dispute-Admin-Dispute-detail-card Dispute-Admin-Dispute-detail-card--messages">

            {/* Tabs */}
            <div className="Dispute-Admin-Dispute-detail-tabs">
              <button
                className={`Dispute-Admin-Dispute-detail-tab ${activeTab === 'messages' ? 'active' : ''}`}
                onClick={() => setActiveTab('messages')}
              >
                Messages
              </button>
              <button
                className={`Dispute-Admin-Dispute-detail-tab ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                Description
              </button>
              <button
                className={`Dispute-Admin-Dispute-detail-tab ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
              >
                Timeline
              </button>
            </div>

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="Dispute-Admin-Dispute-detail-messages">
                <div className="Dispute-Admin-Dispute-detail-message-timestamp">
                  DISPUTE OPENED —{' '}
                  {dispute.created_at
                    ? new Date(dispute.created_at).toLocaleString()
                    : 'N/A'}
                </div>

                {messages.length === 0 && (
                  <p className="Dispute-Admin-Dispute-detail-messages-empty">
                    No messages yet.
                  </p>
                )}

                {messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`Dispute-Admin-Dispute-detail-message ${msg.sender === 'admin' ? 'support' : 'customer'}`}
                  >
                    <div className="Dispute-Admin-Dispute-detail-message-header">
                      <span className="Dispute-Admin-Dispute-detail-message-sender">
                        {msg.sender === 'admin' ? 'Admin Support' : (dispute.customer_name || 'Customer')}
                      </span>
                      <span className="Dispute-Admin-Dispute-detail-message-time">
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </span>
                    </div>
                    {msg.image && (
                      <div className="Dispute-Admin-Dispute-detail-message-attachments">
                        <img
                          src={getImageUrl(msg.image)}
                          alt="attachment"
                          className="Dispute-Admin-Dispute-detail-attachment-image"
                          onClick={() => openImageGallery(msg.image)}
                        />
                      </div>
                    )}
                    {msg.message && (
                      <div className="Dispute-Admin-Dispute-detail-message-text">{msg.message}</div>
                    )}
                  </div>
                ))}

                {imagePreview && (
                  <div className="Dispute-Admin-Dispute-detail-image-preview">
                    <img src={imagePreview} alt="preview" />
                    <button type="button" className="Dispute-Admin-Dispute-detail-image-remove" onClick={handleRemoveImage}>×</button>
                  </div>
                )}

                <div className="Dispute-Admin-Dispute-detail-message-input-container">
                  <button className="Dispute-Admin-Dispute-detail-attachment-button" onClick={handleAttachmentClick}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <input
                    type="text"
                    className="Dispute-Admin-Dispute-detail-message-input"
                    placeholder="Write a message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button className="Dispute-Admin-Dispute-detail-send-button" onClick={handleSendMessage}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M18 2L9 11M18 2l-7 16-2-7-7-2 16-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Description Tab */}
            {activeTab === 'description' && (
              <div className="Dispute-Admin-Dispute-detail-desc-tab">
                <h4 className="Dispute-Admin-Dispute-detail-desc-heading">Customer Description</h4>
                <p className="Dispute-Admin-Dispute-detail-desc-text">
                  {dispute.description || 'No description provided.'}
                </p>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="Dispute-Admin-Dispute-detail-timeline">
                <div className="Dispute-Admin-Dispute-detail-timeline-item">
                  <div className="Dispute-Admin-Dispute-detail-timeline-dot"></div>
                  <div className="Dispute-Admin-Dispute-detail-timeline-content">
                    <div className="Dispute-Admin-Dispute-detail-timeline-title">Dispute Created</div>
                    <div className="Dispute-Admin-Dispute-detail-timeline-time">
                      {dispute.created_at
                        ? new Date(dispute.created_at).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="Dispute-Admin-Dispute-detail-timeline-item">
                  <div className="Dispute-Admin-Dispute-detail-timeline-dot"></div>
                  <div className="Dispute-Admin-Dispute-detail-timeline-content">
                    <div className="Dispute-Admin-Dispute-detail-timeline-title">Last Updated</div>
                    <div className="Dispute-Admin-Dispute-detail-timeline-time">
                      {dispute.updated_at
                        ? new Date(dispute.updated_at).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Image Gallery Modal */}
      {showImageGallery && (
        <div className="Dispute-Admin-Dispute-detail-gallery-overlay" onClick={() => setShowImageGallery(false)}>
          <div className="Dispute-Admin-Dispute-detail-gallery-content" onClick={(e) => e.stopPropagation()}>
            <button className="Dispute-Admin-Dispute-detail-gallery-close" onClick={() => setShowImageGallery(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6l-12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="Dispute-Admin-Dispute-detail-gallery-images">
              {galleryImages.map((img, i) => (
                <img key={i} src={img} alt={`Gallery ${i + 1}`} className="Dispute-Admin-Dispute-detail-gallery-image" />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeDetail;